import numeral from 'numeral'
import { SellableInactiveFragment } from '#src/fragments/SellableInactive.generated'
import { SellablePricingWithDefaultVariantFragment } from '#src/fragments/SellablePricingWithDefaultVariant.generated'
import { SellableUrlFragment } from '#src/fragments/SellableUrl.generated'
import { SellableType } from '../generated/types'
import { SellablePricingFragment } from '#src/fragments/SellablePricing.generated'
import { MonetaryAmountFragment } from '#src/fragments/MonetaryAmount.generated'
import { formatMonetaryAmountAsJsx, MonetaryAmountFormattingOptions } from './money'
import logger from './logger'
import { SellableWithParentFragment } from '#src/fragments/SellableWithParent.generated'
import { SellablePricingWithParentPricingFragment } from '#src/fragments/SellableWithParent.generated'
import { SellableFreeShippingFragment } from '../fragments/SellablePricing.generated'
import { doesDeptSupportFreeShipping } from '#src/services/categories'

interface Sellable {
  type: string
  title?: string
  brand: {
    name: string
    displayName: string
  } | null
  serviceLocation: {
    displayTitle: string | null
  } | null
}

export function getSellableUrl(sellableUrlData: SellableUrlFragment) {
  if (
    sellableUrlData.type !== SellableType.PRODUCT &&
    sellableUrlData.type !== SellableType.SERVICE
  ) {
    throw new Error(
      `Attempted to generate link for unhandled sellable type [${sellableUrlData.type}]`
    )
  }

  let sellableUrl = '/'

  // First determine if this is a product or experience
  sellableUrl += `${sellableUrlData.type === SellableType.PRODUCT ? 'products' : 'experiences'}`

  // If a friendly ID is available, use that, otherwise use the default sellable ID
  sellableUrl += `/${sellableUrlData.friendlyId ? sellableUrlData.friendlyId : sellableUrlData.id}`

  // Slug should always be available, append this to the end in any case
  sellableUrl += `/${sellableUrlData.slug}`

  return sellableUrl
}

/**
 * Returns a title and subtitle of a Sellable based on their type.
 *
 * This function is useful to have consistency data across the website.
 *
 * @param sellable
 */
export function getTitleAndSubtitle(
  sellable: Sellable
): { title?: string; subtitle?: string | null } {
  if (sellable.type === 'SERVICE') {
    return {
      title: sellable.title,
      subtitle: sellable.serviceLocation ? sellable.serviceLocation.displayTitle : undefined
    }
  }

  return {
    title: sellable.brand ? sellable.brand.displayName : undefined,
    subtitle: sellable.title
  }
}

/**
 * Format a Sellable's pricing information
 *
 * `variantsPricesDiffer` is used to display certain copy for a parent sellable.
 * For a select variant, we show the pricing normally with price and show msrp and savings if available.
 * For a parent sellable and `variantsPricesDiffer` === true, we want to show the price and savings as 'From $199 and 'Up to 10% off'. We do not show the msrp. Things list golf sets can have varying msrp.
 * For a parent sellable and `variantsPricesDiffer` === false, pricing is shown as '$199' and savings is shown as '10% off' and we display the msrp if available.
 */
interface FormattedSellablePricing {
  price: string | JSX.Element
  msrp?: string | JSX.Element
  savings?: string | JSX.Element
  savingsPercentage?: string
  variantsPricesDiffer?: boolean
  variantsMsrpDiffer?: boolean
}

export function formatSellablePricing(
  sellable: SellablePricingFragment,
  formattingOptions?: MonetaryAmountFormattingOptions & {
    percentSavingsDisplayThreshold?: number
  },
  normalizationOptions?: NormalizationOptions
): FormattedSellablePricing | null {
  const pricing = normalizeSellablePricing(sellable, normalizationOptions)

  if (!pricing) {
    return null
  }

  const {
    price,
    msrp,
    msrpRange,
    savings,
    savingsPercentage,
    variantsPricesDiffer,
    variantsMsrpDiffer
  } = pricing

  // The savings display threshold is passed in as a whole number
  const displaySavings =
    formattingOptions &&
    formattingOptions.percentSavingsDisplayThreshold &&
    savingsPercentage &&
    savingsPercentage * 100 >= formattingOptions!.percentSavingsDisplayThreshold!

  let formattedMsrp: string | JSX.Element | undefined
  if (normalizationOptions?.pricingBasedOnAllVariants) {
    formattedMsrp =
      msrpRange && displaySavings
        ? variantsMsrpDiffer && price.amount !== msrpRange.min.amount
          ? `${formatMonetaryAmountAsJsx(
              msrpRange.min,
              formattingOptions
            )}-${formatMonetaryAmountAsJsx(msrpRange.max, formattingOptions)}`
          : formatMonetaryAmountAsJsx(msrpRange.max, formattingOptions)
        : undefined
  } else {
    formattedMsrp =
      msrp && displaySavings ? formatMonetaryAmountAsJsx(msrp, formattingOptions) : undefined
  }

  return {
    price: formatMonetaryAmountAsJsx(price, formattingOptions),
    msrp: formattedMsrp,
    savings:
      savings && displaySavings ? formatMonetaryAmountAsJsx(savings, formattingOptions) : undefined,
    savingsPercentage:
      savingsPercentage && displaySavings ? `${Math.round(savingsPercentage * 100)}%` : undefined,
    variantsPricesDiffer,
    variantsMsrpDiffer
  }
}

/**
 * Normalize a Sellable's pricing information
 *
 * This function assumes that the salePrice and originalPrice have the same currency
 */
interface MSRPRange {
  min: MonetaryAmountFragment
  max: MonetaryAmountFragment
}

export interface SellablePricing {
  price: MonetaryAmountFragment
  msrp?: MonetaryAmountFragment
  msrpRange?: MSRPRange
  savings?: MonetaryAmountFragment
  savingsPercentage?: number
  variantsPricesDiffer?: boolean
  variantsMsrpDiffer?: boolean
}

export interface NormalizationOptions {
  pricingBasedOnAllVariants?: boolean
  salePriceOverride?: MonetaryAmountFragment
}

export function normalizeSellablePricing(
  sellable:
    | SellablePricingFragment
    | SellablePricingWithDefaultVariantFragment
    | SellablePricingWithParentPricingFragment,
  options: NormalizationOptions = {}
): SellablePricing | null {
  let { salePrice, originalPrice } = sellable
  let variantsPricesDiffer: boolean | undefined,
    variantsMsrpDiffer: boolean | undefined,
    savings: MonetaryAmountFragment | undefined,
    savingsPercentage: number | undefined,
    msrpRange: MSRPRange | undefined

  salePrice = options.salePriceOverride || salePrice
  const pricingBasedOnAllVariants = options.pricingBasedOnAllVariants

  /**
   * Override salePrice and originalPrice to reflect all of the variants when displaying parent sellables
   */
  if (pricingBasedOnAllVariants && 'variationMatrix' in sellable && sellable.variationMatrix) {
    const priceRange =
      ('priceRange' in sellable.variationMatrix && sellable.variationMatrix.priceRange) || undefined
    variantsPricesDiffer = priceRange ? priceRange.max.amount !== priceRange.min.amount : false
    salePrice =
      priceRange?.min ||
      ('defaultVariant' in sellable.variationMatrix &&
        sellable.variationMatrix.defaultVariant.salePrice) ||
      undefined
    msrpRange =
      ('originalPriceRange' in sellable.variationMatrix &&
        sellable.variationMatrix.originalPriceRange) ||
      undefined
    variantsMsrpDiffer = msrpRange ? msrpRange.max.amount !== msrpRange.min.amount : false
  } else if (pricingBasedOnAllVariants && !('variationMatrix' in sellable)) {
    logger.captureMessage(
      'Attempted to normalize pricing based on all variants without a variation matrix'
    )
  }

  // If there is no pricing information, return null
  if (!salePrice) {
    return null
  }

  /**
   * Calculate savings and savingsPercentage
   */
  if (pricingBasedOnAllVariants && 'variationMatrix' in sellable && sellable.variationMatrix) {
    savings =
      ('maximumSavingsAmount' in sellable.variationMatrix &&
        sellable.variationMatrix.maximumSavingsAmount) ||
      undefined
    savingsPercentage =
      ('maximumSavingsPercent' in sellable.variationMatrix &&
        sellable.variationMatrix.maximumSavingsPercent) ||
      undefined
  } else if (originalPrice) {
    const savingsAmount = parseFloat(originalPrice.amount) - parseFloat(salePrice.amount)
    savings = {
      __typename: 'MonetaryAmount',
      amount: numeral(savingsAmount).format('0,0.00'),
      amountInSmallestDenomination: `${savingsAmount * 100}`,
      currency: salePrice.currency
    }
    savingsPercentage = savingsAmount / parseFloat(originalPrice.amount)
  }

  /**
   * If there is a savings percentage show msrp and savings info
   * For `pricingBasedOnAllVariants`, it's possible that a parent sellable has varying msrp, so we don't
   * show msrp if `variantsPricesDiffer` === true, but still want to show `savingsPercentage`
   */
  if (savingsPercentage) {
    return {
      price: salePrice,
      msrp: originalPrice || undefined,
      msrpRange,
      savings,
      savingsPercentage,
      variantsPricesDiffer,
      variantsMsrpDiffer
    }
  }

  // Otherwise, return just the sale price
  return {
    price: salePrice,
    variantsPricesDiffer
  }
}

/**
 * Is this sellable in stock and available for purchase?
 */
export function isSellableAvailableForPurchase(sellable: SellableInactiveFragment) {
  const isActive = sellable.active
  const isPriced = sellable.salePrice !== null
  const isOutOfStockIncludingPreorders = sellable.outOfStock && !sellable.availableForPreOrder

  return isActive && isPriced && !isOutOfStockIncludingPreorders
}

export function isParentSellable(sellable: SellableWithParentFragment) {
  return !sellable.variant && !!sellable.variationMatrix
}

export function hasFreeShipping(
  sellable: SellableFreeShippingFragment | SellableWithParentFragment
): boolean {
  if (isParentSellable(sellable as SellableWithParentFragment)) {
    return !!(sellable as SellableWithParentFragment).variationMatrix?.sellables.some(sellable =>
      hasFreeShipping(sellable)
    )
  }

  return (
    !!sellable.department &&
    doesDeptSupportFreeShipping(sellable.department.type) &&
    !!sellable.salePrice &&
    parseInt(sellable.salePrice.amount, 10) >= 50
  )
}
